// Typed sugar over `ApiError` for the five most-reached-for error classes.
// Each helper is statically restricted to the ErrorCodes that actually belong
// to its class (`CodeOfClass<...>`), so `notFound("RATE_LIMITED", ...)` is a
// compile error, not a runtime surprise caught in code review. Reach for one
// of these before hand-rolling `new ApiError({ code: "..." })` at a call
// site — the class ends up implied correctly for free.
import { ApiError } from "./ApiError";
import type { ErrorClass, ErrorCode } from "./errorCodes";
import { type errorCodeClass } from "./errorCodes";

// The ErrorCodes whose class is exactly C.
type CodeOfClass<C extends ErrorClass> = {
  [K in ErrorCode]: (typeof errorCodeClass)[K] extends C ? K : never;
}[ErrorCode];

type HelperOptions = { details?: unknown; ref?: string };

const makeHelper =
  <C extends ErrorClass>() =>
  (code: CodeOfClass<C>, options: HelperOptions = {}): ApiError =>
    // Built conditionally, never with an explicit `undefined` value, because
    // `exactOptionalPropertyTypes` treats an optional property's assignable
    // type as exactly `T`, not `T | undefined`.
    new ApiError({
      code,
      ...(options.details !== undefined ? { details: options.details } : {}),
      ...(options.ref !== undefined ? { ref: options.ref } : {}),
    });

/** class `notFound`, e.g. `notFound("POOL_NOT_FOUND", { ref: "#20260805-pool01" })`. */
export const notFound = makeHelper<"notFound">();

/** class `forbidden`, e.g. `forbidden("FORBIDDEN_POOL", { details: { poolId } })`. */
export const forbidden = makeHelper<"forbidden">();

/** class `conflict`. */
export const conflict = makeHelper<"conflict">();

/** class `validation`. Named `validationFailed` — `validation` reads as a noun, not a verb, at a call site. */
export const validationFailed = makeHelper<"validation">();

/** class `rateLimited`. */
export const rateLimited = makeHelper<"rateLimited">();

/** class `lockedOut`. */
export const lockedOut = makeHelper<"lockedOut">();

/** class `entitlementRequired`. */
export const entitlementRequired = makeHelper<"entitlementRequired">();

/** class `providerUnavailable`. */
export const providerUnavailable = makeHelper<"providerUnavailable">();
